import React from 'react';
import { motion } from 'framer-motion';
import { Check, TrendingDown } from 'lucide-react';
import LeadForm from '@/components/LeadForm';

const BULLETS = [
  'A written breakdown of exactly where your spend is leaking',
  'The three fixes that move your account first',
  'A realistic path from your current revenue to $100k/month',
  'Reviewed by a senior strategist — not a template',
];

export default function MetaHero() {
  return (
    <section className="relative bg-[#0B0B0B] pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
      <div className="absolute inset-0 fine-grid opacity-70" />
      <div className="absolute -top-40 left-1/3 w-[640px] h-[640px] bg-[#2563EB]/15 blur-[160px] rounded-full" />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#3B82F6]/40 bg-[#2563EB]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#3B82F6]"
          >
            <TrendingDown className="w-3.5 h-3.5" /> Meta Ads Performance Division
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-7 font-display font-bold text-white tracking-tight text-[2.6rem] sm:text-6xl lg:text-[4.2rem] leading-[1.02]"
          >
            Stop Bleeding Money on Meta Ads —{' '}
            <span className="text-[#3B82F6]">Scale to $100k/Month</span> Instead
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.16 }}
            className="mt-7 text-lg lg:text-2xl text-white/70 font-light leading-relaxed max-w-2xl"
          >
            We identify where your ad spend is bleeding and show you exactly how to scale profitably on Meta.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.24 }}
            className="mt-6 text-white/50 font-light leading-relaxed max-w-xl"
          >
            Ahana Digital Agency helps new and established brands scale to $100k/month with Meta — through spend
            forensics, a disciplined creative engine, and structured scaling that protects your cost per acquisition.
          </motion.p>

          <ul className="mt-9 space-y-3.5">
            {BULLETS.map((b) => (
              <li key={b} className="flex gap-3 items-start">
                <Check className="w-4 h-4 text-[#3B82F6] mt-1 shrink-0" />
                <span className="text-white/65 font-light">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 w-full"
          id="breakdown"
        >
          <div className="rounded-xl border border-white/15 bg-white/[0.04] backdrop-blur-sm p-7 lg:p-9">
            <h2 className="font-display font-bold text-2xl text-white tracking-tight">
              Claim Your Free Meta Ads Breakdown
            </h2>
            <p className="mt-3 text-sm text-white/55 font-light leading-relaxed">
              Three details is all we need. A senior strategist reviews your account approach and replies with the
              findings.
            </p>
            <div className="mt-7">
              <LeadForm type="meta_audit" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}